import {useEffect, useState} from 'react'
import {Lang} from 'yougile-lang'
import {logger, ObjUtil, Util} from 'yougile-util'
import {
  EmailSettingsChangeReq,
  EmailSettingsResp,
  NotifyFrequency
} from 'yougile-model'
import {Backend} from '@common/backend'
import {MessageBox} from '@components/message-box'

type Settings = {
  news: boolean
  notifications: boolean
  frequency: NotifyFrequency
}

const defaultSettings: Settings = {
  notifications: true,
  news: true,
  frequency: 'often'
}

type UseMailSettingsReturnType = {
  loaded: boolean
  settings: Settings
  changed: boolean
  saving: boolean
  save: () => void
  toggleNews: () => void
  toggleNotify: () => void
  setNotifyFrequency: (frequency: NotifyFrequency) => void
}

export const useMailSettings = (
  email: string,
  key: string
): UseMailSettingsReturnType => {
  const [backendSettings, setBackendSettings] = useState(defaultSettings)
  const [settings, setSettings] = useState(defaultSettings)
  const [changed, setChanged] = useState(false)
  const [loaded, setLoaded] = useState(false)
  const [saving, setSaving] = useState(false)

  const save = (): void => {
    if (saving || !changed) {
      return
    }
    setSaving(true)
    const req: EmailSettingsChangeReq = {
      email: Util.cleanEmail(email),
      key,
      notify: settings.notifications ? settings.frequency : 'no',
      news: settings.news
    }
    void Backend.postJSON('/data/mail-settings', req)
      .then(() => {
        setBackendSettings(settings)
        MessageBox.show(Lang.msg.mspSuccess, 'success')
      })
      .catch(error => MessageBox.show('Error: ' + error, 'danger'))
      .then(() => {
        setSaving(false)
      })
  }

  const toggleNews = (): void =>
    setSettings({...settings, news: !settings.news})
  const toggleNotify = (): void =>
    setSettings({...settings, notifications: !settings.notifications})

  const setNotifyFrequency = (frequency: NotifyFrequency): void => {
    if (frequency === 'no') {
      settings.notifications = false
      setSettings({...settings, frequency: 'often'})
      return
    }
    setSettings({...settings, frequency})
  }

  useEffect(() => {
    const url =
      `/data/mail-settings` +
      `?email=${encodeURIComponent(
        Util.cleanEmail(email)
      )}&key=${key}&lang=${Lang.getLang()}`

    Backend.getJSON<EmailSettingsResp>(url)
      .then(res => {
        const backSettings = res.data.settings
        const notifications = backSettings.notify !== 'no'
        settings.notifications = notifications
        settings.frequency = notifications ? backSettings.notify : 'often'
        settings.news = backSettings.news

        setBackendSettings(settings)
        setSettings(settings)
        setLoaded(true)
      })
      .catch(e => logger.error(`Error: ${e}`))
  }, [])

  useEffect(() => {
    setChanged(!ObjUtil.deepEqual(settings, backendSettings, false))
  }, [backendSettings, settings])

  return {
    loaded,
    settings,
    changed,
    saving,
    save,
    toggleNews,
    toggleNotify,
    setNotifyFrequency
  }
}
